/**
 * Where the OG cards live, as URLs. Isomorphic and import-free: the meta of a page names its card with these,
 * and nothing here renders one (the renderer is `scripts/og/`, run by the pack process and the build).
 *
 * A pack's card is a pack asset, `<pack-samples base>/<slug>/<PACK_CARD_FILE>`, written when the pack ships. A surface card
 * (home, pricing, packs, …) is a static file of the product build, `/og/<key>.png`, versioned by the content hash
 * the build wrote into its manifest.
 */

export type OgCardProduct = 'narcis' | 'toile'

/** The file name of a pack's card under its slug, next to the pack's thumbnails. Ovid has none: it generates no card. */
export const PACK_CARD_FILE: Record<OgCardProduct, string> = {narcis: 'og.png', toile: 'og.png'}

export function packOgImageUrl(product: OgCardProduct, samplesBaseUrl: string, slug: string): string {
    return `${samplesBaseUrl}/${slug}/${PACK_CARD_FILE[product]}`
}

/** Replaced at build time by `vite.config.ts` with the surface-card manifest (`key -> content hash`). Undefined when
 *  no build rendered one (jest, the dev server); a build always writes it, empty for a product with no cards. */
declare const __OG_SURFACE_VERSIONS__: Record<string, string> | undefined

/** `?v=<hash>` rides on the URL: the `/og/*` cache policy keys on `v`, so a changed card self-busts. Under a build, a key
 *  the manifest lacks throws: a page must never name a card whose version it cannot give. */
export function surfaceOgImageUrl(baseUrl: string, key: string): string {
    const versions = typeof __OG_SURFACE_VERSIONS__ === 'undefined' ? null : __OG_SURFACE_VERSIONS__
    if (versions === null) return `${baseUrl}/og/${key}.png`
    const version = versions[key]
    if (!version) throw new Error(`No OG surface card '${key}' in the build's manifest (scripts/og/build-surface-cards.ts)`)
    return `${baseUrl}/og/${key}.png?v=${version}`
}
